import { Brick, BrickContext, registerBrick, Transaction, File as OFile, ErrorFlow, CloudObject } from 'olympe';
import { stringToBinary } from '@olympeio/core';
import Papa from 'papaparse';

import Inspection from '../../../models/Inspection';
import Component from '../../../models/Component';
import System from '../../../models/System';
import Evaluation from '../../../models/Evaluation';
import TranslatedString from '../../../models/TranslatedString';


import { CSV_HEADERS } from './constants';

export default class ExportTranslationCSV extends Brick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {*} system
     * @param {function()} forwardEvent
     * @param {function(File)} setResult
     * @param {function(*)} setErrorFlow
     */
    async update($, [system], [forwardEvent, setResult, setErrorFlow]) {
        // Init data
        const data = [];
        // Extract system information
        const systemName = system.getName();

        const sysNameTs = system.followSingle(System.translatedNameRel).executeFromCache();

        // Add System Lines
        data.push(this.lineToObject(sysNameTs, 'System Name'));
        data.push(this.lineDescriptionToObject(system, 'System Description'));

        const rootComponents = system.follow(System.componentsRel).sortBy(Component.rankProp).executeFromCache().toArray();

        // Call fetchDataComponents fn
        this.fetchDataComponents(rootComponents, data, true);

        // Build content
        const content = Papa.unparse(data, {
            'delimiter': ';',
        });

        // Init transaction
        const transaction = new Transaction(false);
        const file = transaction.create(OFile);

        // Convert to Binary format
        const binaryContent = stringToBinary(content);

        OFile.setContent(transaction, file, `${systemName.replace(/\s/g, '')}_Translation.csv`, binaryContent, 'text/csv');

        // Execute transaction
        transaction.execute().then(() => {
            // On success set new result image
            setResult(CloudObject.get(file));
            forwardEvent();
        }).catch((message) => {
            setErrorFlow(ErrorFlow.create(`Failed to create translation file for '${systemName}' - ${message}`, 1));
        });
    }

    /**
     * @private
     * @param {!Component} component
     * @param {Array} data
     * @param {boolean} isRootComponent
     */
    fetchSubComponents(component, data, isRootComponent) {
        const subComponents = component.follow(Component.subComponentsRel).sortBy(Component.rankProp).executeFromCache().toArray();
        this.fetchDataComponents(subComponents, data, isRootComponent);
    }

    /**
     * @private
     * @param {Array<Component>} components
     * @param {Array} data
     * @param {boolean} isRootComponent
     */
    fetchDataComponents(components, data, isRootComponent) {
        for (const component of components) {
            const rootCompNameTs = component.follow(Component.translatedNameRel).executeFromCache().getFirst();

            this.fetchSubComponents(component, data, false);
            // Add Root Component Lines
            data.push(this.lineToObject(rootCompNameTs, isRootComponent ? 'Root Component Name' : 'Sub Component Name'));
            data.push(this.lineDescriptionToObject(component, isRootComponent ? 'Root Component Description' : 'Sub Component Description'));

            // Process Inspections
            const inspections = component.follow(Component.inspectionsRel).sortBy(Inspection.rankProp).executeFromCache().toArray();

            for (const inspection of inspections) {
                // Extract inspection information
                const inspectionNameTs = inspection.follow(Inspection.translatedNameRel).executeFromCache().getFirst();

                // Add inspection Lines
                data.push(this.lineToObject(inspectionNameTs, 'Inspection Name'));
                data.push(this.lineDescriptionToObject(inspection, 'Inspection Description'));

                // Process Evaluations
                const evaluationsList = inspection.follow(Inspection.evaluationsListRel).executeFromCache().toArray();

                for (const evaluation of evaluationsList) {
                    // Extract evaluation information
                    const evaluationNameTs = evaluation.follow(Evaluation.translatedNameRel).executeFromCache().getFirst();

                    // Add Evaluation Lines
                    data.push(this.lineToObject(evaluationNameTs, 'Result Name'));
                    data.push(this.lineDescriptionToObject(evaluation, 'Result Description'));
                }
            }
        }
    }

    /**
     * @private
     * @param {TranslatedString} translatedString
     * @param {string} itemName
     * @return {object}
     */
    lineToObject(translatedString, itemName) {
        // Extract translated values
        const { ITEM, EN_VALUE, FR_VALUE, DE_VALUE, IT_VALUE, ZH_VALUE, ES_VALUE, PT_VALUE, VI_VALUE, TR_VALUE, TAG, DESCRIPTION } = CSV_HEADERS;
        
        // Construct line
        const line = {};
        line[ITEM] = itemName;
        line[DESCRIPTION] = '';
        line[TAG] = translatedString.getTag();
        line[EN_VALUE] = translatedString?.get(TranslatedString.enProp) ?? '';
        line[FR_VALUE] = translatedString?.get(TranslatedString.frProp) ?? '';
        line[DE_VALUE] = translatedString?.get(TranslatedString.deProp) ?? '';
        line[IT_VALUE] = translatedString?.get(TranslatedString.itProp) ?? '';
        line[ZH_VALUE] = translatedString?.get(TranslatedString.zhProp) ?? '';
        line[ES_VALUE] = translatedString?.get(TranslatedString.esProp) ?? '';
        line[PT_VALUE] = translatedString?.get(TranslatedString.ptProp) ?? '';
        line[VI_VALUE] = translatedString?.get(TranslatedString.viProp) ?? '';
        line[TR_VALUE] = translatedString?.get(TranslatedString.trProp) ?? '';
        return line;
    }

    /**
     * @private
     * @param {Inspection Entity|Evaluation} entity
     * @param {string} itemName
     * @return {object}
     */
    lineDescriptionToObject(entity, itemName) {
        // Extract translated values
        const { ITEM, TAG, DESCRIPTION } = CSV_HEADERS;

        // Construct line
        const line = {};
        line[ITEM] = itemName;
        line[DESCRIPTION] = entity?.getDescription() || '';
        line[TAG] = entity.getTag();
        return line;
    }
}

registerBrick('018a8e09e57c85300cec', ExportTranslationCSV);
